import { normalizePlatformAllocationKey } from "./mediaPlanAllocations";
import { PLATFORM_BRANDING, PLATFORM_HERO_IMAGES } from "./platformBranding";
import { resolvePlatformStrategyValue } from "./micrositePlatformStrategyCard";

// Presentation aliases only. This registry never selects or expands a channel:
// the caller must pass the SAME resolved cards rendered in the platform plan.
// Exact matching is intentional: an unknown "New Display Exchange" must not
// inherit Display's identity just because its name contains a familiar word.
const identityToken = (value) => value.trim().normalize("NFC").toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");
const nonEmptyText = (value) => typeof value === "string" ? value.trim() : "";

// Shared with the detailed platform plan: display precision does not alter
// saved allocation numbers or the visual weights derived from them.
export const formatAllocationPercentLabel = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return `${Number.isInteger(numeric) ? numeric.toFixed(0) : numeric.toFixed(1)}%`;
};

export const CAMPAIGN_MEDIA_NETWORK_ALIASES = Object.freeze({
  ...Object.fromEntries(Object.entries(PLATFORM_BRANDING).flatMap(([key, brand]) => [
    [identityToken(key), key],
    [identityToken(brand.label), key],
  ])),
  metafacebookinstagram: "meta",
  metafacebookandinstagram: "meta",
  facebookinstagram: "meta",
  facebookandinstagram: "meta",
  metafamily: "meta",
  metaplatforms: "meta",
  metaigfb: "meta",
  fb: "facebook",
  ig: "instagram",
  googlesearch: "google_search",
  googlepaidsearch: "google_search",
  paidsearch: "google_search",
  search: "google_search",
  sem: "google_search",
  google: "google_search",
  openai: "chatgpt_search",
  chatgpt: "chatgpt_search",
  connectedtv: "ctv",
  connectedtelevision: "ctv",
  connectedtvctv: "ctv",
  ctvott: "ctv",
  ott: "ctv",
  streaming: "ctv",
  streamingtv: "ctv",
  fast: "ctv",
  ctvstreamingfast: "ctv",
  ctvstreaming: "ctv",
  streamingfast: "ctv",
  streamingaudio: "audio",
  digitalaudio: "audio",
  podcast: "audio",
  banner: "display",
  onlinevideo: "video",
  programmaticvideo: "video",
  programmaticvideoolv: "video",
  videoolv: "video",
  olv: "video",
  inapp: "mobile",
  mobiledisplay: "mobile",
  mobileads: "mobile",
  inappmobile: "mobile",
  mobileinapp: "mobile",
  outofhome: "dooh",
  digitaloutofhome: "dooh",
  digitaloutofhomecinema: "dooh",
  doohcinema: "dooh",
  cinema: "dooh",
  ooh: "dooh",
  yahoodsp: "yahoo",
  yahoodspdisplay: "yahoo",
  yahoodspctv: "yahoo",
  amazonads: "amazon",
  amazondsp: "amazon",
  amazondisplay: "amazon",
  amazonvideo: "amazon",
  amazonprimevideo: "amazon",
  viantdsp: "viant",
  viantdspdisplay: "viant",
  viantdisplay: "viant",
  viantdspctv: "viant",
  twitter: "x",
  twitterads: "x",
  xtwitter: "x",
  twitterx: "x",
  xads: "x",
  linkedinads: "linkedin",
  reddit: "reddit",
  stackadapt: "stackadapt",
  programmatic: "programmatic",
  reservetest: "reserve_test",
  reserve: "reserve_test",
  test: "reserve_test",
  testing: "reserve_test",
  contingency: "reserve_test",
});

// Governed display identifiers. The branding registry retires the legacy ctv
// phrasings ("CTV / OTT", "Connected TV") and Viant's channel suffix from
// presentation, and the Cortex plan cards already render exactly "CTV" and
// "Viant" for them (normalizeSherlocPlatformDisplayLabel). Every other
// platform keeps the label saved on its card, so a node reads the way the
// card below it does.
const GOVERNED_DISPLAY_LABEL_KEYS = Object.freeze(["ctv", "viant"]);

// Only still artwork belongs in the network: an animated legacy logo (DOOH's
// GIF) would keep moving under prefers-reduced-motion. The platform's static
// hero derivative of the same governed artwork stands in for it; with no
// still asset at all the node keeps the generic media icon.
const stillAsset = (asset) =>
  typeof asset === "string" && asset && !/\.gif(?:$|[?#])/i.test(asset) ? asset : null;

export const resolveCampaignPlatformIdentity = (platform) => {
  const card = platform && typeof platform === "object" && !Array.isArray(platform)
    ? platform : null;
  const raw = card
    ? [card.key, card.platformKey, card.platform_key, card.platform, card.title, card.label,
      card.platformName, card.platform_name, card.platformLabel, card.platform_label, card.name, card.channel]
      .map(nonEmptyText).find(Boolean)
    : nonEmptyText(platform);
  if (!raw) return null;
  const token = identityToken(raw);
  const mapped = Object.prototype.hasOwnProperty.call(CAMPAIGN_MEDIA_NETWORK_ALIASES, token)
    ? CAMPAIGN_MEDIA_NETWORK_ALIASES[token] : null;
  // The existing allocation normalizer remains the family identity authority.
  // Retain the child identity for saved detail and allocation matching. Family
  // identity is a separate presentation projection, never a saved-data rewrite.
  const knownKey = mapped && (["facebook", "instagram"].includes(mapped)
    ? mapped : normalizePlatformAllocationKey(mapped, { allowUnknown: true }));
  const unknownIdentity = raw.normalize("NFC").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "_").replace(/^_+|_+$/g, "");
  const savedLabel = card
    ? [card.title, card.label, card.platformLabel, card.platform_label, card.platform,
      card.platformName, card.platform_name, card.name, card.channel, raw]
      .map(nonEmptyText).find(Boolean)
    : raw;
  return {
    raw,
    id: knownKey || `unknown:${unknownIdentity || raw.toLowerCase()}`,
    key: knownKey || unknownIdentity || "media",
    known: Boolean(knownKey),
    label: knownKey && GOVERNED_DISPLAY_LABEL_KEYS.includes(knownKey)
      ? PLATFORM_BRANDING[knownKey].label
      : savedLabel,
  };
};

export const resolveCampaignPlatformFamilyIdentity = (platform) => {
  const identity = resolveCampaignPlatformIdentity(platform);
  if (!identity) return null;
  return ["meta", "facebook", "instagram"].includes(identity.key)
    ? { ...identity, id: "meta", key: "meta", label: "Meta" }
    : identity;
};

/** The served campaign.platforms value owns current saved strategy scope,
 * including explicit empty arrays/strings. Older artifacts may supply structured
 * saved scope or saved role cards. Draft selections, allocation rows and prose
 * never supply destinations. Meta grouping is a new presentation object only;
 * the saved Facebook/Instagram selections are never rewritten.
 */
export const normalizeCampaignPlatformScope = (campaign = {}, savedCards = []) => {
  const candidates = [
    campaign?.platforms, campaign?.targetPlatforms, campaign?.target_platforms,
    campaign?.strategyPayload?.platforms, campaign?.strategy_payload?.platforms,
    campaign?.strategyPayload?.targetPlatforms, campaign?.strategy_payload?.target_platforms,
    campaign?.aiPlan?.platforms, campaign?.aiPlan?.targetPlatforms, campaign?.aiPlan?.target_platforms,
  ];
  const sourceIndex = candidates.findIndex((value) => Array.isArray(value) || typeof value === "string");
  const source = sourceIndex >= 0 ? candidates[sourceIndex] : Array.isArray(savedCards) ? savedCards : [];
  const entries = typeof source === "string" ? source.split(/\s*,\s*/) : source;
  const selected = new Map();
  entries.forEach((platform) => {
    const identity = resolveCampaignPlatformFamilyIdentity(platform);
    if (!identity) return;
    const item = selected.get(identity.id) || {
      key: identity.known ? identity.key : identity.raw, label: identity.label,
    };
    if (identity.key === "meta") {
      // Keep which children were selected in this derived object, so grouping
      // Facebook cannot admit a stale Instagram detail card (or vice versa).
      item.memberKeys = [...new Set([...(item.memberKeys || []), resolveCampaignPlatformIdentity(platform).key])];
    }
    selected.set(identity.id, item);
  });
  return [...selected.values()];
};

export const filterCampaignPlatformCards = (cards = [], scope = []) => {
  const allowed = new Map();
  scope.forEach((platform) => {
    const family = resolveCampaignPlatformFamilyIdentity(platform);
    if (!family) return;
    const members = Array.isArray(platform?.memberKeys)
      ? platform.memberKeys : [resolveCampaignPlatformIdentity(platform).key];
    allowed.set(family.id, new Set([...(allowed.get(family.id) || []), ...members]));
  });
  return (Array.isArray(cards) ? cards : []).filter((card) => {
    const identity = resolveCampaignPlatformFamilyIdentity(card);
    const members = identity && allowed.get(identity.id);
    if (!members) return false;
    const rawKey = resolveCampaignPlatformIdentity(card).key;
    return identity.key !== "meta" || rawKey === "meta" || members.has("meta") || members.has(rawKey);
  });
};

// Expand only the selected child identities retained by normalization. Cards
// and the rest of the microsite keep that detail; only the network groups it.
// An explicitly saved Meta plan remains a single family selection.
export const expandCampaignPlatformScope = (scope = []) => scope.flatMap((platform) => {
  const identity = resolveCampaignPlatformFamilyIdentity(platform);
  const members = platform?.memberKeys;
  if (identity?.key !== "meta" || !Array.isArray(members) || members.includes("meta")) return [platform];
  return members.filter((key) => ["facebook", "instagram"].includes(key))
    .map((key) => ({ key, label: PLATFORM_BRANDING[key].label }));
});

// A saved family card covers its children; a child card covers a family plan,
// but it cannot cover a separately selected sibling. Share this presence rule
// between the existing card fallback and minimal missing-card completion.
export const findMissingCampaignPlatformSelections = (cards = [], scope = []) => {
  const present = new Set(cards.map(resolveCampaignPlatformIdentity).filter(Boolean).map(({ id }) => id));
  return expandCampaignPlatformScope(scope).filter((platform) => {
    const identity = resolveCampaignPlatformIdentity(platform);
    if (!identity) return false;
    const covered = present.has(identity.id) ||
      (["facebook", "instagram"].includes(identity.key) && present.has("meta")) ||
      (identity.key === "meta" && (present.has("facebook") || present.has("instagram")));
    if (covered) return false;
    present.add(identity.id);
    return true;
  });
};

export const completeCampaignPlatformCards = (cards = [], scope = []) => {
  const selected = filterCampaignPlatformCards(cards, scope);
  return [...selected, ...findMissingCampaignPlatformSelections(selected, scope).map((platform) => {
    const identity = resolveCampaignPlatformIdentity(platform);
    // Preserve an absent plan as absent: no generated role, allocation or
    // readiness claim is needed to retain an explicitly saved destination.
    return { key: identity.known ? identity.key : identity.raw, title: identity.label };
  })];
};

// The existing microsite's requested-allocation source precedence. Return
// raw rows: its display normalizer may derive percentages from labels/budgets,
// which are not allocation evidence for this presentation.
export const selectCampaignRequestedAllocationRows = (campaign = {}) => {
  const chartReady = campaign?.chartReady || {};
  const budget = campaign?.budgetArchitecture || campaign?.sections?.budgetArchitecture || {};
  return [
    campaign?.requestedChannelAllocation, campaign?.requested_channel_allocation,
    chartReady?.requestedChannelAllocation, chartReady?.requested_channel_allocation,
    budget?.requestedChannelAllocation, budget?.requested_channel_allocation,
  ].find((rows) => Array.isArray(rows) && rows.length) || [];
};

// The explicit percentage fields already accepted by mediaPlanAllocations.
// Keep one list for raw evidence and saved-card corroboration.
const EXPLICIT_ALLOCATION_PERCENT_FIELDS = Object.freeze([
  "percent", "percentage", "allocationPercent", "allocation_percent", "budgetPercent",
  "budget_percent", "budgetShare", "sharePercent", "share_percent",
]);

// Only explicitly typed percentage fields are accepted. Display strings,
// currency, share/value/weight, and estimated or budget-derived figures are
// deliberately ignored. Nothing is normalized to 100 or split between nodes.
const explicitAllocationPercent = (row) => {
  if (!row || typeof row !== "object" || row.estimated || row.isEstimatedFallback ||
      row.allocationPercentReconciled === false) return null;
  const source = nonEmptyText(row.allocationPercentSource || row.allocationSource);
  if (source && !["allocation_authority", "requested_channel_allocation", "structured"].includes(source)) {
    return null;
  }
  const values = EXPLICIT_ALLOCATION_PERCENT_FIELDS.map((field) => row[field])
    .filter((value) => value !== undefined && value !== null &&
      (typeof value !== "string" || Boolean(value.trim())));
  // Conflicting aliases are not reliable evidence; no field silently overrides
  // another or changes the existing platform plan's source precedence.
  return values.length && values.every((value) => typeof value === "number" &&
    Number.isFinite(value) && value >= 0 && value <= 100 && value === values[0])
    ? values[0] : null;
};

// Child detail can use only its own raw saved row, never a grouped Meta share.
// Preserve any allocation already carried by the saved detail card; this fills
// a missing presentation label without rewriting its financial fields.
export const resolveCampaignMetaChildAllocationPercent = (card, allocations = []) => {
  const identity = resolveCampaignPlatformIdentity(card);
  if (!["facebook", "instagram"].includes(identity?.key)) return null;
  const savedAllocationFields = [
    "canonicalAllocationLabel", "allocationLabel", "shareLabel",
  ];
  if (savedAllocationFields.some((field) => {
    const value = card?.[field];
    return value !== undefined && value !== null &&
      (typeof value !== "string" || Boolean(value.trim()));
  })) return null;
  const exactRows = (Array.isArray(allocations) ? allocations : [])
    .filter((row) => resolveCampaignPlatformIdentity(row)?.id === identity.id);
  const values = exactRows.map(explicitAllocationPercent);
  if (!values.length || !values.every((value) => value !== null && value === values[0])) return null;
  // A saved numeric share may gain a missing display label only when the raw
  // row corroborates it. Conflicting saved values remain untouched.
  // share/allocation are established saved-card financial fields. They may
  // corroborate an explicit raw percentage, but never supply one themselves.
  const savedValues = [...EXPLICIT_ALLOCATION_PERCENT_FIELDS, "share", "allocation"]
    .map((field) => card?.[field]).filter((value) =>
    value !== undefined && value !== null && (typeof value !== "string" || Boolean(value.trim()))
  );
  return savedValues.every((value) => value === values[0]) ? values[0] : null;
};

/**
 * Project the resolved platform-plan cards into destinations. Meta-family
 * detail stays in the saved cards, with one family node in this illustration.
 * Optional raw structured
 * allocations used ONLY to enrich those nodes. Neither argument is mutated.
 * No campaign, artifact, prose, budget or provider lookup is performed here.
 */
export const buildCampaignMediaNetwork = (platforms, allocations = [], selectedScope = platforms) => {
  if (!Array.isArray(platforms)) return [];
  const selectedMetaMembers = [...new Set(expandCampaignPlatformScope(
    Array.isArray(selectedScope) ? selectedScope : []
  ).map(resolveCampaignPlatformIdentity).filter((identity) =>
    ["meta", "facebook", "instagram"].includes(identity?.key)
  ).map(({ id }) => id))];
  const allocationByIdentity = new Map();
  (Array.isArray(allocations) ? allocations : []).forEach((row) => {
    const identity = resolveCampaignPlatformIdentity(row);
    if (!identity) return;
    const values = allocationByIdentity.get(identity.id) || [];
    values.push(explicitAllocationPercent(row));
    allocationByIdentity.set(identity.id, values);
  });
  const groups = [];
  let metaGroup;
  platforms.forEach((card) => {
    const identity = resolveCampaignPlatformFamilyIdentity(card);
    if (!identity) return;
    if (identity.key === "meta" && metaGroup) {
      metaGroup.cards.push(card);
    } else {
      const group = { identity, cards: [card] };
      groups.push(group);
      if (identity.key === "meta") metaGroup = group;
    }
  });
  const usedKeys = new Set();
  return groups.map(({ identity, cards }) => {
    let key = identity.key;
    let duplicate = 1;
    while (usedKeys.has(key)) key = `${identity.key}_${++duplicate}`;
    usedKeys.add(key);
    let values = allocationByIdentity.get(identity.id);
    const rawIdentities = cards.map(resolveCampaignPlatformIdentity);
    if (!values && identity.key === "meta" && selectedMetaMembers.length === 1 && selectedMetaMembers[0] !== "meta") {
      // A child share applies only to an explicitly child-only saved scope.
      // A partial detail card cannot establish the allocation of a Meta plan.
      values = allocationByIdentity.get(selectedMetaMembers[0]);
    }
    // Resolved cards can carry legacy percentages enriched from parsed prose
    // or budget labels. Only the separately supplied raw allocation rows are
    // percentage evidence; a missing row is always neutral, never a card fallback.
    const allocationPercent = values
      ? (values.every((value) => value !== null && value === values[0]) ? values[0] : null)
      : null;
    const representative = identity.key === "meta"
      ? rawIdentities.find(({ key: rawKey }) => rawKey === "meta") : rawIdentities[0];
    const roles = cards.map((card, index) => ({
      label: rawIdentities[index].label,
      role: resolvePlatformStrategyValue(card, "role") || resolvePlatformStrategyValue(card, "funnelPurpose"),
    })).filter(({ role }) => role);
    return {
      key,
      label: representative?.label || identity.label,
      image: identity.known
        ? stillAsset(PLATFORM_BRANDING[identity.key]?.image) || stillAsset(PLATFORM_HERO_IMAGES[identity.key])
        : null,
      role: roles.length > 1
        ? [...new Set(roles.map(({ label, role }) => `${label}: ${role}`))].join(" ")
        : roles[0]?.role || "",
      allocationPercent,
    };
  });
};
